import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAttendanceSummaryForDate, deleteAttendance } from '../api/attendanceApi';
import { listAllStudents } from '../api/studentsApi';
import { sendAbsentReportEmail } from '../api/adminApi';
import { matchesSearch } from '../utils/searchFilter';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function AttendanceSummarySection({ setMsg, setErr, allowDelete = false }) {
  const [date, setDate] = useState(todayIso());
  const [summary, setSummary] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [deletingAttId, setDeletingAttId] = useState('');
  const [filter, setFilter] = useState('');

  const studentNameMap = useMemo(() => {
    const m = new Map();
    for (const s of students) {
      m.set(String(s.studentId), s.studentName);
      m.set(String(s.admno), s.studentName);
    }
    return m;
  }, [students]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [sum, st] = await Promise.all([getAttendanceSummaryForDate(date), listAllStudents()]);
      setSummary(sum);
      setStudents(st);
    } catch (e) {
      setErr(e.message || 'Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [date, setErr]);

  useEffect(() => {
    load();
  }, [load]);

  const absentRows = useMemo(() => {
    if (!summary) return [];
    const rows = [];
    for (const c of summary.classes) {
      for (const id of c.absentStudents) {
        rows.push({
          studentId: id,
          studentName: studentNameMap.get(String(id)) || id,
          class: c.class,
        });
      }
    }
    return rows.sort((a, b) => String(a.class).localeCompare(String(b.class)));
  }, [summary, studentNameMap]);

  const filteredAbsent = useMemo(
    () => absentRows.filter((r) => matchesSearch(filter, [r.studentName, r.studentId, r.class])),
    [absentRows, filter]
  );

  const handleSendEmail = async () => {
    setSending(true);
    setMsg('');
    setErr('');
    try {
      const result = await sendAbsentReportEmail({ date });
      setMsg(result.message || `Absent report sent for ${date}.`);
    } catch (e) {
      setErr(e.message || 'Could not send email. Deploy Cloud Functions and set email secrets.');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteClassAttendance = async (row) => {
    if (!row.attendanceId) return;
    const label = `${date} · class ${row.class}`;
    if (!window.confirm(`Delete attendance for ${label}?`)) return;
    setMsg('');
    setErr('');
    setDeletingAttId(row.attendanceId);
    try {
      await deleteAttendance(row.attendanceId);
      setMsg(`Deleted attendance: ${label}`);
      await load();
    } catch (e) {
      setErr(e.message || 'Could not delete attendance');
    } finally {
      setDeletingAttId('');
    }
  };

  return (
    <>
      <div className="mps-card">
        <h2>Daily attendance summary</h2>
        <p className="mps-muted" style={{ marginTop: 0 }}>
          View school-wide attendance for any date. Absent list is emailed automatically at <strong>5:00 PM IST</strong> to{' '}
          <code>mpsvns1995@gmail.com</code>.
        </p>
        <div className="mps-grid" style={{ alignItems: 'end' }}>
          <div className="mps-field">
            <label>Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <button type="button" className="mps-btn mps-btn-secondary" onClick={load} disabled={loading}>
              Refresh
            </button>
            <button
              type="button"
              className="mps-btn"
              style={{ marginLeft: 8 }}
              onClick={handleSendEmail}
              disabled={sending || loading}
            >
              {sending ? 'Sending…' : 'Email absent list now'}
            </button>
          </div>
        </div>
        {loading ? (
          <p style={{ marginTop: 16 }}>Loading…</p>
        ) : summary ? (
          <div className="mps-summary-cards" style={{ marginTop: 16 }}>
            <div className="mps-summary-card">
              <div className="mps-summary-value">{summary.totals.classCount}</div>
              <div className="mps-summary-label">Classes marked</div>
            </div>
            <div className="mps-summary-card mps-summary-present">
              <div className="mps-summary-value">{summary.totals.present}</div>
              <div className="mps-summary-label">Present</div>
            </div>
            <div className="mps-summary-card mps-summary-absent">
              <div className="mps-summary-value">{summary.totals.absent}</div>
              <div className="mps-summary-label">Absent</div>
            </div>
            <div className="mps-summary-card">
              <div className="mps-summary-value">{summary.totals.marked}</div>
              <div className="mps-summary-label">Total marked</div>
            </div>
          </div>
        ) : null}
      </div>

      {summary && summary.classes.length > 0 ? (
        <div className="mps-card">
          <h2>Class-wise breakdown</h2>
          <table className="mps-table">
            <thead>
              <tr>
                <th>Class</th>
                <th>Present</th>
                <th>Absent</th>
                <th>Total</th>
                {allowDelete ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {summary.classes.map((c) => (
                <tr key={c.class}>
                  <td>{c.class}</td>
                  <td>{c.presentStudents.length}</td>
                  <td>{c.absentStudents.length}</td>
                  <td>{c.presentStudents.length + c.absentStudents.length}</td>
                  {allowDelete ? (
                    <td>
                      <button
                        type="button"
                        className="mps-btn mps-btn-sm mps-btn-danger"
                        disabled={deletingAttId === c.attendanceId}
                        onClick={() => handleDeleteClassAttendance(c)}
                      >
                        {deletingAttId === c.attendanceId ? 'Deleting…' : 'Delete'}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="mps-card">
        <h2>Absent students ({absentRows.length})</h2>
        <input
          type="search"
          className="mps-table-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search name, adm no, class…"
        />
        {absentRows.length === 0 ? (
          <p className="mps-muted">No absent students recorded for this date (or attendance not marked yet).</p>
        ) : (
          <table className="mps-table">
            <thead>
              <tr>
                <th>Adm no.</th>
                <th>Name</th>
                <th>Class</th>
              </tr>
            </thead>
            <tbody>
              {filteredAbsent.map((r) => (
                <tr key={`${r.class}-${r.studentId}`}>
                  <td>{r.studentId}</td>
                  <td>{r.studentName}</td>
                  <td>{r.class}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
